import { Alert, Box, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Flex from "@/components/Flex";
import LabeledSwitch from "@/components/LabeledSwitch";
import PageLayout from "@/components/PageLayout";
import CustomTabs, { TabData } from "@/components/app/CustomTabs";
import CustomTitle from "@/components/app/CustomTitle";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import CptCodeCatalogDrawer from "@/components/app/admin/Library/catalog/CptCodeCatalogDrawer";
import DiagnosisCodeCatalogDialog from "@/components/app/admin/Library/catalog/DiagnosisCodeCatalogDrawer";
import DrugCatalogDrawer from "@/components/app/admin/Library/catalog/DrugCatalogDrawer";
import HcpcsCatalogDrawer from "@/components/app/admin/Library/catalog/HcpcsCatalogDrawer";
import LoincCodeCatalogDrawer from "@/components/app/admin/Library/catalog/LoincCodeCatalogDrawer";
import { useRoleController } from "@/components/useRoleController";
import { ButtonDataType } from "@/constants/constant-types";
import { useDebounce } from "@/hooks/use-debounce";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  CATALOG_IMPORT_QUERY_KEY,
  CATALOG_IMPORT_STARTED_DISPLAY_MS,
  CATALOG_IMPORT_STARTED_SUFFIX,
  CATALOG_IMPORT_TAB_HINT,
  CATALOG_TAB_IMPORT_NOTICE_SUFFIX,
  CATALOG_TABS,
} from "@/pages/app/admin/library/catalog/catalog-constant";
import { CatalogTab } from "@/pages/app/admin/library/catalog/catalog-types";
import { usePendingCatalogImports } from "@/pages/app/admin/library/catalog/hooks/use-pending-catalog-imports";
import { useImportCatalogDataFromAdmin } from "@/queries/catalog/use-import-catalog";
import { getErrorSnackbar, getSuccessSnackbar, isAdmin } from "@/utils/utils";
import SearchInput from "@/components/SearchInput";
import { getStoredRealm, getViewAs } from "@/utils/auth-storage";

const CatalogLayout = () => {
  const [isArchieved, setIsArcheived] = useState(false);
  const navigate = useNavigate();
  const viewAs = getViewAs();
  const isProviderView = viewAs === "provider";
  const [
    lionCodeCatalogDisclosure,
    drugCodeCatalogDisclosure,
    icdTenCodeCatalogDisclosure,
    cptCodeCatalogDisclosure,
    hcpcsCodeCatalogDisclosure,
  ] = useReduceDisclosure(5);

  const { isImportPending, markImported } = usePendingCatalogImports();
  const { mutateAsync: importData } = useImportCatalogDataFromAdmin();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const TABS_DATA: (ButtonDataType & CatalogTab)[] = CATALOG_TABS.map(
    (tab) => ({
      ...tab,
      path: viewAs === "admin" ? tab.adminPath : tab.providerPath,
    })
  );
  const [activeTab, setActiveTab] = useState(
    TABS_DATA.find((ele) => ele.path === location.pathname) ?? TABS_DATA[0]
  );
  const [search, setSearch, instantSearchVal] = useDebounce<string>("");
  useRoleController("Master Data");
  const { hasUpdate: hasMasterDataUpdate } = useRoleController(
    "Master Data > CPT Codes"
  );

  const tabsData: TabData[] = useMemo(
    () =>
      CATALOG_TABS.map((tab) => ({
        value: tab.value,
        label: tab.label,
        icon:
          isProviderView && isImportPending(tab.importType) ? (
            <Tooltip title={CATALOG_IMPORT_TAB_HINT} arrow>
              <Box
                component="span"
                role="img"
                aria-label={CATALOG_IMPORT_TAB_HINT}
                sx={{ width: 8, height: 8, borderRadius: "50%" }}
                bgcolor="error.main"
              />
            </Tooltip>
          ) : undefined,
      })),
    [isProviderView, isImportPending]
  );

  useEffect(() => {
    const curTab = TABS_DATA.find((it) => it.path === location.pathname);
    setActiveTab(curTab ?? TABS_DATA[0]);
    setSearch("");
  }, [location.pathname]);

  function dialogOpenHandler(label: string) {
    if (label === "Add LOINC Code") lionCodeCatalogDisclosure.open();
    else if (label === "Add Diagnosis Code") icdTenCodeCatalogDisclosure.open();
    else if (label === "Add CPT Code") cptCodeCatalogDisclosure.open();
    else if (label === "Add HCPCS Code") hcpcsCodeCatalogDisclosure.open();
    else if (label === "Add Drug") drugCodeCatalogDisclosure.open();
  }

  const handleImport = () => {
    const { importType } = activeTab;
    showSnackbar({
      type: "success",
      message: `${importTabName} ${CATALOG_IMPORT_STARTED_SUFFIX}`,
    });
    const startedAlertCleared = new Promise((resolve) =>
      setTimeout(resolve, CATALOG_IMPORT_STARTED_DISPLAY_MS)
    );
    importData({ xTENANTID: getStoredRealm() ?? undefined, type: importType })
      .then(async (res) => {
        queryClient.invalidateQueries({ queryKey: [CATALOG_IMPORT_QUERY_KEY] });
        markImported(importType);
        await startedAlertCleared;
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch(async (err) => {
        await startedAlertCleared;
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const importTabName = activeTab.label?.split(" ")?.[0] ?? "";
  const importButtonName = `Import ${importTabName} Data`;

  const isActiveTabImportPending =
    isProviderView && isImportPending(activeTab.importType);

  return (
    <PageLayout
      header={
        <>
          <Flex width={"100%"} justifyContent={"space-between"}>
            <CustomTitle>Catalog</CustomTitle>
            <Box>
              {isProviderView && (
                <StyledButtonV2
                  label={importButtonName}
                  onClick={handleImport}
                  shouldAddIcon
                  variant="outlined"
                />
              )}
            </Box>
          </Flex>
          {isActiveTabImportPending && (
            <Alert severity="warning" role="status" sx={{ mt: "10px" }}>
              {`${activeTab.label} ${CATALOG_TAB_IMPORT_NOTICE_SUFFIX}`}
            </Alert>
          )}
          <Flex
            justifyContent={"space-between"}
            gap={1}
            mt={"10px"}
            alignItems={"center"}
          >
            <CustomTabs
              tabsData={tabsData}
              value={activeTab?.value}
              onChange={(data) => {
                const activeTabData =
                  TABS_DATA.find((value) => value.value === data) ??
                  TABS_DATA[0];
                navigate(activeTabData?.path);
              }}
            />
            <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
              <LabeledSwitch
                label="Show Archived Only"
                checked={isArchieved}
                onChange={(e) => setIsArcheived(e.target.checked)}
              />

              <SearchInput
                searchKey={instantSearchVal}
                onSearch={(val) => setSearch(val)}
                placeholder={`Search ${activeTab.label ?? ""}`}
                sx={{ width: "226px" }}
              />
              {!isAdmin() && hasMasterDataUpdate && (
                <StyledButtonV2
                  label={activeTab.addButtonLabel}
                  onClick={() => dialogOpenHandler(activeTab.addButtonLabel)}
                  shouldAddIcon
                  sx={{ whiteSpace: "nowrap" }}
                />
              )}

              {isAdmin() && (
                <StyledButtonV2
                  label={activeTab.addButtonLabel}
                  onClick={() => dialogOpenHandler(activeTab.addButtonLabel)}
                  shouldAddIcon
                  sx={{ whiteSpace: "nowrap" }}
                />
              )}
            </Box>
          </Flex>

          <Box>
            <LoincCodeCatalogDrawer
              dialogTitle={"Add LOINC Code"}
              disclosureHost={lionCodeCatalogDisclosure}
            />
            <DrugCatalogDrawer
              dialogTitle={"Add Drug Catalog"}
              disclosureHost={drugCodeCatalogDisclosure}
            />
            <DiagnosisCodeCatalogDialog
              disclosureHost={icdTenCodeCatalogDisclosure}
              dialogTitle={"Add Diagnosis Code"}
            />
            <CptCodeCatalogDrawer
              disclosureHost={cptCodeCatalogDisclosure}
              dialogTitle={"Add CPT Code"}
            />
            <HcpcsCatalogDrawer
              disclosureHost={hcpcsCodeCatalogDisclosure}
              dialogTitle={"Add HCPCS Code"}
            />
          </Box>
          {/* <Box marginTop={"0.725rem"} /> */}
        </>
      }
    >
      <Outlet context={{ search, isArchieved }} />
    </PageLayout>
  );
};

export default CatalogLayout;
